import { useNavigate } from 'react-router-dom';
import { useTranslations } from '../../i18n';
import { theme } from '../../lib/theme';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { StickyNote, EyeOff, ImageIcon, Wrench, Bell } from 'lucide-react';
import { SupportedLanguage } from '../../lib/expenseTypeHelpers';

interface NoteItemProps {
    note: {
        id: string;
        title?: string;
        content: string;
        is_pinned?: boolean;
        is_public?: boolean;
        created_at: string;
        updated_at: string;
        image_count?: number;
        spare_part_count?: number;
        // For Dashboard compatibility (it uses camelCase currently, need to standardize or map)
        imageCount?: number;
        partCount?: number;
        reminders?: any[];
    };
    slug?: string;
    isAdminView?: boolean;
    compact?: boolean; // For dashboard view maybe?
    openInNewTab?: boolean;
    carIsPublic?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    className?: string;
    highlighted?: boolean;
    forceShowYear?: boolean;
}

export function NoteItem({
    note,
    slug,
    isAdminView = false,
    compact = false,
    openInNewTab = false,
    carIsPublic = false,
    onClick,
    className = '',
    highlighted = false,
    forceShowYear = false
}: NoteItemProps) {
    const navigate = useNavigate();
    const { t, language } = useTranslations();

    // Normalize fields between Dashboard and NotesList usage
    const lines = (note.content || '').split('\n');
    // If title is explicitly provided (dashboard), use it. Otherwise derive from content.
    // Actually, dashboard `note` prop has `title` as string. NotesList `note` has `title` (derived in render there, but data model has `title` field which is often "Note" or explicit).
    // NotesList Logic:
    const firstLine = lines[0] || '';
    const rawTitle = firstLine.replace(/[#*_`~]/g, '').trim().substring(0, 100) || 'Note';
    // Dashboard currently uses note.title.
    // We should prefer the logic from NotesList for consistency.

    const displayTitleRaw = compact && note.title && note.title !== 'Note' ? note.title : rawTitle;
    const displayTitle = displayTitleRaw.length > 40 ? displayTitleRaw.substring(0, 40) + '...' : displayTitleRaw;

    // Body content:
    const bodyContent = lines.slice(1).join('\n').trim();

    // Counts
    const imgCount = note.image_count !== undefined ? note.image_count : (note.imageCount || 0);
    const prtCount = note.spare_part_count !== undefined ? note.spare_part_count : (note.partCount || 0);

    // Date
    // NotesList uses updated_at. Dashboard uses created_at.
    // Let's use updated_at if available, else created_at.
    const dateStr = note.updated_at || note.created_at;

    const getReminderStatus = () => {
        if (!note.reminders || note.reminders.length === 0) return null;
        const r = note.reminders[0];
        // Compare dates (ignore time)
        const remindDateStr = typeof r.remind_date === 'string' ? r.remind_date.split('T')[0] : '';
        const todayStr = new Date().toISOString().split('T')[0];
        const isPast = remindDateStr < todayStr;
        const isActive = r.is_active && (!isPast || r.repeat_yearly);
        return isActive ? 'active' : 'inactive';
    };
    const reminderStatus = getReminderStatus();

    const navigateToNote = (e: React.MouseEvent) => {
        if (onClick) {
            onClick(e);
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        const prefix = isAdminView ? '/admin' : '';
        if (slug) {
            const url = `${prefix}/${slug}/notes/${note.id}`;
            if (openInNewTab) {
                window.open(url, '_blank');
            } else {
                navigate(url);
            }
        }
    };

    const getPreviewContent = (content: string) => {
        if (!content) return t.notes.emptyNote;
        return content.length > 200 ? content.substring(0, 200) + '...' : content;
    };

    return (
        <button
            type="button"
            onClick={navigateToNote}
            className={`w-full group text-left flex items-start gap-4 ${compact ? 'p-4' : theme.spacing.cardPadding} transition-all ${highlighted ? 'bg-yellow-50 dark:bg-yellow-500/10' : 'hover:bg-gray-50 dark:hover:bg-white/5'} active:scale-[0.99] ${className}`}
        >
            <div className={`${theme.spacing.iconContainer} rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0`}>
                <StickyNote size={20} className="text-blue-600 dark:text-blue-400" />
            </div>

            <div className="flex-1 min-w-0 flex items-start gap-3">
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <h4 className={`${theme.typography.itemTitle} truncate`}>
                        {displayTitle}
                    </h4>

                    {/* If compact (Dashboard), maybe we typically showed less? 
                But user wants to "use the same". 
                "Dashboard usually showed title + date + counts".
                Now we show Title + Description + Metadata column.
                This is richer. Let's use it.
            */}
                    <div className="prose prose-sm prose-slate dark:prose-invert max-w-none text-[13px] text-gray-500 dark:text-gray-400 italic font-medium leading-relaxed tiptap-preview line-clamp-2 overflow-hidden text-ellipsis">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {getPreviewContent(bodyContent)}
                        </ReactMarkdown>
                    </div>
                </div>

                <div className="flex flex-col items-end gap-0.5 flex-shrink-0 pt-0.5">
                    <div className="flex items-center gap-2">
                        {/* Dashboard didn't show Public/Private icon usually, but NotesList did. 
                    If compact/dashboard, maybe show it too? It's useful info.
                */}
                        {carIsPublic && note.is_public === false && (
                            <EyeOff size={14} className="text-red-500" />
                        )}

                        <span className={`${theme.typography.itemSubtitle} whitespace-nowrap`}>
                            {(() => {
                                const date = new Date(dateStr);
                                const currentYear = new Date().getFullYear();
                                const noteYear = date.getFullYear();
                                const showYear = forceShowYear || noteYear !== currentYear;
                                return date.toLocaleDateString(language as SupportedLanguage, { 
                                    day: '2-digit', 
                                    month: 'short',
                                    year: showYear ? 'numeric' : undefined
                                });
                            })()}
                        </span>
                    </div>

                    {(imgCount > 0 || prtCount > 0 || reminderStatus) && (
                        <div className="flex items-center gap-2">
                            {imgCount > 0 && (
                                <span className="flex items-center gap-1 text-[13px] font-medium text-blue-500 dark:text-blue-400">
                                    <ImageIcon size={14} />
                                    {imgCount}
                                </span>
                            )}
                            {prtCount > 0 && (
                                <span className="flex items-center gap-1 text-[13px] font-medium text-orange-500">
                                    <Wrench size={14} />
                                    {prtCount}
                                </span>
                            )}
                            {reminderStatus === 'active' && (
                                <Bell size={14} className="text-blue-500" />
                            )}
                            {reminderStatus === 'inactive' && (
                                <Bell size={14} className="text-gray-400" />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </button>
    );
}
